import { clone, flow, getSnapshot, types, runInAction } from 'mobx-state-tree';
import { Interaction } from './Meeting';
import { ApiStore } from './ApiStore';
import { NullVideo, NullVideoChunk, Video, VideoChunck } from './VideoPresentation';
import * as Sentry from '@sentry/react';

// Retry an async operation on transient failure with exponential backoff.
// `shouldRetry` gates which errors are worth retrying (default: all of them);
// `onRetry` runs just before each backoff sleep.
const retryWithBackoff = async (operation, { attempts = 3, baseDelayMs = 500, shouldRetry = () => true, onRetry } = {}) => {
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;
            if (attempt === attempts || !shouldRetry(error)) break;
            if (onRetry) onRetry(error, attempt);
            await new Promise(resolve => setTimeout(resolve, baseDelayMs * Math.pow(2, attempt - 1)));
        }
    }
    throw lastError;
};

// How long a part PUT may go quiet before it is treated as wedged. While bytes
// are still moving the socket keeps firing progress events even on a slow link,
// because the OS send buffer autotunes down to the bandwidth-delay product. Once
// the whole blob has been handed to the socket no more events can arrive, so the
// wait switches to a fixed deadline for S3's response. A false abort costs one
// re-PUT of the part, never data.
const PUT_NO_PROGRESS_MS = 60 * 1000;
const PUT_RESPONSE_MS = 120 * 1000;

// Upload a single multipart part via its presigned PUT URL. Resolves with the
// cleaned ETag; rejects on network error, non-200, or a missing ETag header so
// the caller can retry. Re-PUTting the same part number is idempotent on S3.
//
// `onXhr` hands the live request to the store so reset() can abort a PUT that is
// still hanging on a dead connection; `onSettled` releases it again. Both must be
// MST actions — they run from XHR callbacks, outside any action context.
const putPart = (uploadUrl, blob, partNumber, { onXhr, onSettled } = {}) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    let timer = null;

    const clearStallTimer = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const armStallTimer = (ms) => {
        clearStallTimer();
        timer = setTimeout(() => {
            timer = null;
            // Flagged before abort() so onabort can tell a wedged connection
            // (retry it) from a reset (drop it).
            xhr.__stalled = true;
            xhr.abort();
        }, ms);
    };

    const settle = (finish, value) => {
        if (settled) return;
        settled = true;
        clearStallTimer();
        if (onSettled) onSettled(xhr);
        finish(value);
    };

    xhr.open('PUT', uploadUrl);
    if (xhr.upload) {
        xhr.upload.onprogress = (event) => {
            if (settled) return;
            armStallTimer(event.total && event.loaded >= event.total ? PUT_RESPONSE_MS : PUT_NO_PROGRESS_MS);
        };
    }
    // abort() and network failures reach readyState DONE with status 0 and fire
    // readystatechange BEFORE onabort/onerror. Classifying them here would produce
    // a status-0 error, so let the dedicated handlers name the failure instead.
    xhr.onabort = () => settle(reject, new Error(xhr.__stalled ? 'stall' : 'aborted'));
    xhr.onerror = () => settle(reject, new Error('network'));
    xhr.onreadystatechange = () => {
        if (xhr.readyState !== XMLHttpRequest.DONE) return;
        if (xhr.status === 0) return;
        if (xhr.status === 200) {
            const rawETag = xhr.getResponseHeader('ETag');
            if (!rawETag) {
                settle(reject, new Error(`Upload part ${partNumber} returned no ETag header`));
                return;
            }
            settle(resolve, rawETag.replace(/"/g, ''));
        } else {
            const error = new Error(`Upload part ${partNumber} failed with status ${xhr.status}`);
            error.status = xhr.status;
            settle(reject, error);
        }
    };
    if (onXhr) onXhr(xhr);
    xhr.send(blob);
    // Start the clock only once the request is on its way. A tiny final part may
    // never fire a progress event at all, so this first window covers it too.
    armStallTimer(PUT_NO_PROGRESS_MS);
});

// Which PUT failures are worth another attempt with a fresh presigned URL.
//   stall   — our own no-progress timeout; the connection is wedged, not the request.
//   network — the browser gave up on the socket.
//   400     — S3 closes a PUT whose body stops arriving for ~20s with
//             `400 RequestTimeout`. On a congested uplink with a working downlink
//             (the 2026-08-27 pattern) that arrives BEFORE the 60s stall timer, so
//             treating it as terminal would lose the recording with a tidy error.
//   5xx / no status — transient, as before.
// A reset (`aborted`) and any other 4xx (403 bad signature) won't succeed on a
// re-send, so don't re-upload the blob for them.
const isRetriablePutError = error => {
    if (error.message === 'aborted') return false;
    if (error.message === 'stall' || error.message === 'network') return true;
    return error.status === undefined || error.status === 400 || error.status >= 500;
};

// The retry unit is init-part + PUT together, so this predicate sees both kinds of
// failure. `aborted` means a new recording session took over — never retry it.
// `terminal` marks a failure that re-sending cannot fix. An init-part call that got
// an HTTP response is not retried (unchanged from today); everything else falls
// through to the PUT rules.
const shouldRetryUploadAttempt = error => {
    if (!error) return false;
    if (error.message === 'aborted' || error.terminal) return false;
    if (error.response) return false;
    return isRetriablePutError(error);
};

const videoPresentation = types.model('VideoPresentationStore', {
    videoData: types.optional(Video, getSnapshot(NullVideo)),
    videoEtagData: types.optional(types.array(types.frozen()), []),
    videoChunck: types.optional(VideoChunck, getSnapshot(NullVideoChunk)),
    loading: types.optional(types.boolean, false),
    error: types.optional(types.string, ''),
    // Terminal upload outcome flags. `uploadFailed` means the recording can
    // never produce an assessment (missing part URL, failed PUT, failed
    // finalize) — the recording screen must stop polling and show the error
    // instead of waiting 12 minutes for an assessment that won't exist.
    uploadFailed: types.optional(types.boolean, false),
    finalized: types.optional(types.boolean, false),
    recordingType: types.optional(types.string, ''),
    uploadProgress: types.optional(types.string, 'Video Uploading...'),
    participants: types.optional(types.array(types.frozen()), []),
    interactions: types.optional(types.array(Interaction), []),  // Store MST model instances
    respondedStartTimes: types.optional(types.array(types.number), [])  // Track which interactions the user clicked
});

export const VideoPresentationStore = types.compose(videoPresentation, ApiStore)
    // Not persisted and never snapshotted: `sessionId` identifies one recording
    // attempt so work left over from the previous one can be told apart and
    // dropped, and `currentXhr` is the part PUT that reset() has to abort.
    .volatile(() => ({
        sessionId: 0,
        currentXhr: null
    }))
    .views(self => ({
        get getAppEtags() {
            return self.videoEtagData;
        }
    }
    ))
    .actions((self) => ({
        // XHR callbacks run outside any action, so the store may only be touched
        // from these two named actions (MST's assertWritable would otherwise throw
        // inside the PUT promise and leave it unsettled).
        setCurrentXhr(xhr) {
            self.currentXhr = xhr;
        },
        clearCurrentXhr(xhr) {
            // A stale PUT settling after reset() must not release the new one.
            if (!xhr || self.currentXhr === xhr) self.currentXhr = null;
        },
        reset() {
            // Every flow already in flight belongs to the previous session; bumping
            // the id first means anything that resumes after this point sees itself
            // as stale and writes nothing. Aborting the hung PUT also frees the
            // connection for the new recording.
            self.sessionId += 1;
            if (self.currentXhr) {
                self.currentXhr.abort();
                self.currentXhr = null;
            }
            // Clear all state when switching between meeting types
            self.videoData = getSnapshot(NullVideo);
            self.videoEtagData.clear();
            self.videoChunck = getSnapshot(NullVideoChunk);
            self.loading = false;
            self.error = '';
            self.uploadFailed = false;
            self.finalized = false;
            self.uploadProgress = 'Video Uploading...';
            self.participants.clear();
            self.interactions.clear();
            self.respondedStartTimes.clear();
            console.log('VideoPresentationStore: State cleared for new recording session');
        },
        setRecordingType(recordingType) {
            self.recordingType = recordingType;
        },
        setParticipants(participants) {
            self.participants = participants;
        },
        setInteractions(interactions) {
            // Now expects safe interaction snapshots (plain objects)
            self.interactions = interactions || [];
        },
        markInteractionResponded(startTime) {
            if (!self.respondedStartTimes.includes(startTime)) {
                self.respondedStartTimes.push(startTime);
            }
        },
        uploadInitialChunk: flow(function* uploadInitialChunk(data) {
            self.videoEtagData = [];
            const body = {
                userId: data.userId,
                ContentType: data.ContentType,
                numberOfTries: data.numberOfTries,
                companyId: data.companyId,
                interactions: getInteractionDtos(self.interactions),
                interactionId: data.interactionId  // Include interaction ID for Murphy question tracking
            };
            const sid = self.sessionId;
            self.loading = true;
            self.error = '';
            try {
                const res = clone(NullVideo);
                yield self.postApi(`/companies/${data.companyId}/initialize-upload/${data.key}`, body, res);
                // Navigating away during init used to populate the NEXT session's
                // videoData with the abandoned recording's uploadId.
                if (sid !== self.sessionId) return;
                self.videoData = res;
            } catch (error) {
                if (sid !== self.sessionId) return;
                Sentry.captureException(error);
                console.error('Error fetching data:', error);
                self.error = error.message;
            } finally {
                if (sid === self.sessionId) self.loading = false;
            }
        }),
        uploadChunk: flow(function* uploadChunk(data, blob, isFinal = false) {
            // Parts are stamped with the session that queued them (createBlob), not
            // the session that happens to be current when the queue finally reaches
            // them. Without that, a part queued before reset() would start afterwards,
            // adopt the new session and PUT an old blob into the new multipart.
            const sid = data.sessionId === undefined ? self.sessionId : data.sessionId;
            if (sid !== self.sessionId) {
                console.warn(`uploadChunk: dropping part ${data.partNumber} from a previous recording session.`);
                return;
            }

            // One terminal part means the multipart can never complete. Without
            // this, every part still queued would burn its own three attempts and
            // hold the confirm dialog busy for minutes after the outcome was decided.
            if (self.uploadFailed) {
                console.warn(`uploadChunk: skipping part ${data.partNumber}; this upload has already failed.`);
                return;
            }

            // The multipart is already complete. A part can only arrive here when a
            // confirm raced the finalize and queued the tail chunk a second time
            // (2026-09-15: a Safari double-tap sent the same last 1 MB as part 3 and
            // part 4; S3 answered the duplicate with 404 NoSuchUpload and the screen
            // told the rep a graded recording "didn't upload"). Its bytes are in the
            // object already, so drop it without touching the verdict.
            if (self.finalized) {
                console.warn(`uploadChunk: skipping part ${data.partNumber}; the upload is already finalized.`);
                return;
            }

            // Validate that we have the necessary video data before proceeding
            if (!self.videoData || !self.videoData.key) {
                console.error('uploadChunk called without proper video data initialization. Call uploadInitialChunk first.');
                self.error = 'Video upload not initialized. Please start recording again.';
                return;
            }

            const body = { userId: data.userId, ContentType: data.ContentType, PartNumber: data.partNumber, uploadId: self.videoData.uploadId };
            self.loading = true;
            self.error = '';
            try {
                const keyWithoutExtension = self.videoData.key.split('/')[1].split('.')[0];
                // One retry unit = init-part + PUT. Every attempt takes a FRESH
                // presigned URL (the old one may have expired, and re-issuing one is
                // idempotent on the backend), and the part number is fixed by the
                // caller, so a re-PUT is last-write-wins on S3 rather than a duplicate
                // entry. Nothing inside the closure may write to the store: a retry
                // resumes after a setTimeout, outside any action context.
                const res = yield retryWithBackoff(
                    async () => {
                        if (sid !== self.sessionId) throw new Error('aborted');
                        const part = await self.postApi(`/companies/${data.companyId}/initialize-upload/${keyWithoutExtension}`, body);
                        if (!part || !part.uploadUrl) {
                            // A part with no presigned URL can never be uploaded — the
                            // multipart can never complete. Silently skipping finalize
                            // here left the recording screen polling for a nonexistent
                            // assessment; mark the upload terminally failed instead.
                            const error = new Error(`Upload initialization for part ${data.partNumber} returned no upload URL.`);
                            error.terminal = true;
                            throw error;
                        }
                        if (sid !== self.sessionId) throw new Error('aborted');
                        const ETag = await putPart(part.uploadUrl, blob, data.partNumber, {
                            onXhr: xhr => self.setCurrentXhr(xhr),
                            onSettled: xhr => self.clearCurrentXhr(xhr)
                        });
                        return { uploadUrl: part.uploadUrl, key: part.key, ETag };
                    },
                    {
                        shouldRetry: shouldRetryUploadAttempt,
                        // Retries are otherwise invisible: Sentry only hears about
                        // the exhausted case, so leave a trail a forensic pass can
                        // read out of the console.
                        onRetry: (error, attempt) =>
                            console.warn(`upload part ${data.partNumber} retry`, attempt, error.message)
                    }
                );
                if (sid !== self.sessionId) return;
                self.videoChunck = { uploadUrl: res.uploadUrl, key: res.key || '' };
                self.addVideoEtagData({ PartNumber: data.partNumber, ETag: res.ETag });
                if (isFinal) {
                    yield self.finalizeUpload(data);
                }
            } catch (error) {
                // A reset cancelled this part: the recording it belonged to is gone,
                // so writing `error`/`uploadFailed` here would surface a failure for a
                // recording the user deliberately abandoned.
                if (sid !== self.sessionId || error?.message === 'aborted') {
                    console.warn(`uploadChunk: part ${data.partNumber} cancelled by a new recording session.`);
                    return;
                }
                // Finalize completed while this PUT was in flight: S3 rejects a part
                // for a completed multipart (404 NoSuchUpload), but the object exists
                // and grading is already under way. Not a failure.
                if (self.finalized) {
                    console.warn(`uploadChunk: part ${data.partNumber} failed after finalize; the object is already complete.`, error.message);
                    return;
                }
                Sentry.captureException(error);
                console.error('Error uploading chunk:', error);
                self.error = error.message;
                self.uploadFailed = true;
            } finally {
                if (sid === self.sessionId) self.loading = false;
            }
        }),
        addVideoEtagData(item) {
            self.videoEtagData.push(item);
        },
        finalizeUpload: flow(function* finalizeUpload(data) {
            const sid = self.sessionId;
            const body = {
                uploadId: self.videoData.uploadId,
                // The key the uploadId belongs to, set once per session by
                // createMultipartUpload. videoChunck.key is per-part and is still ''
                // when no part was ever initialised, which is how a reset mid-upload
                // used to POST an empty key and take a 400 from the backend.
                key: self.videoData.key,
                parts: self.videoEtagData,
                respondedStartTimes: self.respondedStartTimes.slice()  // Which interactions the user clicked
            };
            self.error = '';
            if (!body.key || !body.uploadId) {
                // The upload state was cleared out from under this flow. There is no
                // multipart left to complete, so never send the request: the backend
                // would 400 and the screen would poll for 12 minutes for an assessment
                // that cannot exist.
                self.error = 'Upload state was lost before finalize.';
                self.uploadFailed = true;
                return;
            }
            if (Array.isArray(self.videoEtagData) && self.videoEtagData.length > 0) {
                self.loading = true;
                try {
                    // Retry only transient network/timeout failures (no HTTP response). A finalize
                    // request that reached the server completes the upload there even if the client
                    // never gets the response, so re-issuing it after an HTTP error would hit an
                    // already-completed multipart (NoSuchUpload) — don't retry those.
                    yield retryWithBackoff(
                        () => self.postApi(`/companies/${data.companyId}/finalize-upload`, body),
                        { shouldRetry: error => !error.response }
                    );
                    if (sid !== self.sessionId) return;
                    self.finalized = true;
                    self.setUploadProgress('Video upload complete. Sending to Murphy for Grading');
                } catch (error) {
                    if (sid !== self.sessionId) return;
                    Sentry.captureException(error);
                    console.error('Error fetching data:', error);
                    // Deliberately NOT uploadFailed: a finalize that errored may
                    // still have completed server-side (the request reached S3),
                    // in which case the S3 event pipeline grades normally — the
                    // recording screen polls with `uploadUncertain` for this case.
                    self.error = error.message;
                } finally {
                    if (sid === self.sessionId) self.loading = false;
                }
            } else {
                // Zero parts = nothing ever reached S3. Setting loading=true
                // outside this guard used to stick the spinner on forever.
                self.error = 'No video data was captured.';
                self.uploadFailed = true;
            }
        }),
        setUploadProgress(progress) {
            self.uploadProgress = progress;
        },
        getQuestionAudio: flow(function* getQuestionAudio(questionTitle, voiceTag, format) {
            self.error = '';
            try {
                console.log(`Getting audio for question: "${questionTitle}" with voice: ${voiceTag}`);
                const response = yield self.getApi(`/presigned-url?assetType=murphy-audio&key=audio/questions/${questionTitle}_${voiceTag}.${format}`);

                const responseJSON = JSON.parse(response.body);

                if (!responseJSON.url) {
                    console.log(responseJSON);
                    throw new Error(`Failed to get signed URL for audio file!`);
                }

                return responseJSON.url; // Changed from signedUrl to url to match Lambda response
            } catch (error) {
                console.error('Error fetching audio URL:', error);
                self.error = error.message;
                return null;
            }
        })
    }));

const getInteractionDtos = (interactionItems) => {
    return interactionItems?.map(item => ({
        text: item.text,
        // title: item.title, // Uncomment if you have a title property
        participant: item.participantName
    }));
};

